'use client';

// Зачем: клиентская обёртка над PHP signaling-эндпоинтами /api/p2p/*.
// Используется хостом для создания/закрытия лобби и обоими сторонами для обмена SDP/ICE.

import { API_URL } from '@/lib/api';
import { getValidAccessToken } from '@/lib/authStorage';

export type MrP2PSignalMessage = {
  id: string;
  tsMs: number;
  fromId: string;
  toId: string;
  type: 'offer' | 'answer' | 'candidate' | 'leave' | string;
  payload: unknown;
};

export type MrP2PPeerInfo = {
  peerId: string;
  userId: number;
  userName: string;
  joinedAtMs: number;
};

export type MrP2PCreateOptions = {
  lobbyId?: string;
  mapId?: string;
  botCount?: number;
  botsOnly?: boolean;
  maxPeers?: number;
};

export type MrP2PCreateResponse = {
  ok: true;
  lobbyId: string;
  hostId: string;
  hostToken: string;
};

export type MrP2PJoinResponse = {
  ok: true;
  lobbyId: string;
  hostId: string;
  peerId: string;
  peerToken: string;
};

export type MrP2PLobbyInfo = {
  ok: true;
  lobbyId: string;
  hostId: string;
  hostUserId: number;
  hostUserName: string;
  mapId: string;
  botCount: number;
  botsOnly: boolean;
  peerCount: number;
  maxPeers: number;
};

export type MrP2PPollResponse = {
  ok: true;
  now: number;
  lastMs: number;
  messages: MrP2PSignalMessage[];
  peers: MrP2PPeerInfo[];
  hostId: string;
};

function trimSlash(value: string | undefined): string {
  return String(value ?? '').trim().replace(/\/+$/, '');
}

/** Внешний signaling (VPS) или KBSU — см. NEXT_PUBLIC_P2P_SIGNAL_BASE_URL */
function p2pSignalApiRoot(): string {
  const custom = trimSlash(process.env.NEXT_PUBLIC_P2P_SIGNAL_BASE_URL);
  return custom || API_URL;
}

function buildUrl(path: string, params?: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && `${v}` !== '') qs.set(k, String(v));
    }
  }
  const tail = qs.toString();
  return `${p2pSignalApiRoot()}${path}${tail ? `?${tail}` : ''}`;
}

async function fetchJson<T>(input: string, init?: RequestInit & { signal?: AbortSignal }): Promise<T> {
  const token = getValidAccessToken();
  const res = await fetch(input, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const raw = await res.text();
    let detail: unknown;
    try {
      detail = JSON.parse(raw);
    } catch {
      detail = raw;
    }
    throw new Error(`P2P signaling error ${res.status}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
  }
  return (await res.json()) as T;
}

export async function mrP2pCreate(options: MrP2PCreateOptions = {}): Promise<MrP2PCreateResponse> {
  return fetchJson<MrP2PCreateResponse>(buildUrl('/api/p2p/lobby/create'), {
    method: 'POST',
    body: JSON.stringify(options),
  });
}

export async function mrP2pInfo(lobbyId: string): Promise<MrP2PLobbyInfo> {
  return fetchJson<MrP2PLobbyInfo>(buildUrl(`/api/p2p/lobby/${encodeURIComponent(lobbyId)}`));
}

export async function mrP2pJoin(lobbyId: string): Promise<MrP2PJoinResponse> {
  return fetchJson<MrP2PJoinResponse>(buildUrl(`/api/p2p/lobby/${encodeURIComponent(lobbyId)}/join`), {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export type MrP2PResumeHostResponse = {
  ok: true;
  lobbyId: string;
  hostId: string;
  hostToken: string;
};

export async function mrP2pResumeHost(lobbyId: string): Promise<MrP2PResumeHostResponse> {
  return fetchJson<MrP2PResumeHostResponse>(buildUrl(`/api/p2p/lobby/${encodeURIComponent(lobbyId)}/resume_host`), {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function mrP2pSendSignal(
  lobbyId: string,
  fromId: string,
  token: string,
  toId: string,
  type: MrP2PSignalMessage['type'],
  payload: unknown,
): Promise<void> {
  await fetchJson<{ ok: true }>(buildUrl(`/api/p2p/lobby/${encodeURIComponent(lobbyId)}/signal`), {
    method: 'POST',
    body: JSON.stringify({ fromId, token, toId, type, payload }),
  });
}

export async function mrP2pPoll(
  lobbyId: string,
  forId: string,
  since: number,
  waitSec: number,
  signal?: AbortSignal,
): Promise<MrP2PPollResponse> {
  return fetchJson<MrP2PPollResponse>(
    buildUrl(`/api/p2p/lobby/${encodeURIComponent(lobbyId)}/poll`, { forId, since, wait: waitSec }),
    { signal },
  );
}

function parseIcePayload(data: unknown): RTCConfiguration | null {
  const d = data as {
    ok?: boolean;
    iceServers?: RTCIceServer[];
    iceTransportPolicy?: string;
  };
  if (!Array.isArray(d.iceServers) || d.iceServers.length === 0) return null;
  const policy = d.iceTransportPolicy === 'relay' ? 'relay' : 'all';
  return { iceServers: d.iceServers, iceTransportPolicy: policy };
}

// Зачем: приоритет внешнего JSON (coturn на VPS), затем KBSU /api/p2p/ice, иначе публичный STUN.
export async function mrP2pFetchRtcConfiguration(signal?: AbortSignal): Promise<RTCConfiguration> {
  const fallback: RTCConfiguration = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ],
  };

  const externalIce = trimSlash(process.env.NEXT_PUBLIC_P2P_ICE_URL);
  if (externalIce) {
    try {
      const res = await fetch(externalIce, { signal, credentials: 'omit' });
      if (res.ok) {
        const cfg = parseIcePayload(await res.json());
        if (cfg) return cfg;
      }
    } catch {
      /* следующий источник */
    }
  }

  try {
    const res = await fetch(buildUrl('/api/p2p/ice'), { signal, credentials: 'include' });
    if (!res.ok) throw new Error(String(res.status));
    const cfg = parseIcePayload(await res.json());
    if (cfg) return cfg;
  } catch {
    /* fallback */
  }
  return fallback;
}

export async function mrP2pClose(lobbyId: string, hostToken: string): Promise<void> {
  await fetchJson<{ ok: true }>(buildUrl(`/api/p2p/lobby/${encodeURIComponent(lobbyId)}/close`), {
    method: 'POST',
    body: JSON.stringify({ hostToken }),
  });
}
